import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { FlightFilterType } from 'constants/flight'

export function getFlightFilterLabel(intl: IntlShape, filterType: string): string {
  switch (filterType) {
    case FlightFilterType.STOPS:
      return intl.formatMessage({
        id: 'K84Yu8jxhzhG63ZWarEk',
        defaultMessage: 'Stops',
      })
    case FlightFilterType.AIRLINES:
      return intl.formatMessage({
        id: 'wkCJftBBBsQFOKsnYh-F',
        defaultMessage: 'Airlines',
      })
    case FlightFilterType.STOP_DURATIONS:
      return intl.formatMessage({
        id: 'Aa5iL9bqMiplIDCNkl24',
        defaultMessage: 'Stopover time',
      })
    case FlightFilterType.CABIN_TYPES:
      return intl.formatMessage({
        id: 'bPRbwvO76tpiKetu1dSD',
        defaultMessage: 'Cabin',
      })
    case FlightFilterType.DURATIONS:
      return intl.formatMessage({
        id: 'mMuAK41SDCFsKhlS8tey',
        defaultMessage: 'Duration',
      })
    case FlightFilterType.PRICES:
      return intl.formatMessage({
        id: 'JiYh6GiOiiXw6ZkJq-8p',
        defaultMessage: 'Price',
      })
    case FlightFilterType.TIMES:
      return intl.formatMessage({
        id: 'eRZJ5-HzQ3c5mgDqYA-6',
        defaultMessage: 'Times',
      })
    case FlightFilterType.BAGS:
      return intl.formatMessage({
        id: '73SVYIfDEZ-GaNLtHMzX',
        defaultMessage: 'Bags',
      })
    case FlightFilterType.MEMBER_PRICES:
      return intl.formatMessage({
        id: 'pjM-j2wgHZSUzn7bmabJ',
        defaultMessage: 'LuxPlus',
      })
    case FlightFilterType.STOP_OVER_CITIES:
      return intl.formatMessage({
        id: '0xNUpByF-xRkbDmLvKov',
        defaultMessage: 'Stopover cities',
      })
    case FlightFilterType.ALLIANCES:
      return intl.formatMessage({
        id: '9j2ILO0aHLk8uchC_1Km',
        defaultMessage: 'Alliance',
      })
    case FlightFilterType.RECOMMENDED:
      return intl.formatMessage({
        id: '6rlXlAYARUJTptemDPfQ',
        defaultMessage: 'Recommended',
      })
    case FlightFilterType.APPLIED_FILTERS:
      return intl.formatMessage({
        id: 'PEUoj680xvxQUcsG2RIr',
        defaultMessage: 'Applied filters',
      })
    default:
      return filterType
  }
}

export function getFlightFilterModalLabel(intl: IntlShape, filterType: string): string {
  if (filterType === FlightFilterType.DURATIONS) {
    return intl.formatMessage({
      id: '56ESkKjoWIq3of529Lrd',
      defaultMessage: 'Flight duration',
    })
  }
  if (filterType === FlightFilterType.RECOMMENDED) {
    return getFlightFilterLabel(intl, FlightFilterType.RECOMMENDED)
  }
  return getFlightFilterLabel(intl, filterType)
}

function useFlightFilterLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const flightFiltersLabelMap: Record<string, string> = {
      [FlightFilterType.STOPS]: getFlightFilterLabel(intl, FlightFilterType.STOPS),
      [FlightFilterType.AIRLINES]: getFlightFilterLabel(intl, FlightFilterType.AIRLINES),
      [FlightFilterType.STOP_DURATIONS]: getFlightFilterLabel(intl, FlightFilterType.STOP_DURATIONS),
      [FlightFilterType.CABIN_TYPES]: getFlightFilterLabel(intl, FlightFilterType.CABIN_TYPES),
      [FlightFilterType.DURATIONS]: getFlightFilterLabel(intl, FlightFilterType.DURATIONS),
      [FlightFilterType.PRICES]: getFlightFilterLabel(intl, FlightFilterType.PRICES),
      [FlightFilterType.TIMES]: getFlightFilterLabel(intl, FlightFilterType.TIMES),
      [FlightFilterType.BAGS]: getFlightFilterLabel(intl, FlightFilterType.BAGS),
      [FlightFilterType.MEMBER_PRICES]: getFlightFilterLabel(intl, FlightFilterType.MEMBER_PRICES),
      [FlightFilterType.STOP_OVER_CITIES]: getFlightFilterLabel(intl, FlightFilterType.STOP_OVER_CITIES),
      [FlightFilterType.ALLIANCES]: getFlightFilterLabel(intl, FlightFilterType.ALLIANCES),
      [FlightFilterType.APPLIED_FILTERS]: getFlightFilterLabel(intl, FlightFilterType.APPLIED_FILTERS),
      [FlightFilterType.RECOMMENDED]: getFlightFilterLabel(intl, FlightFilterType.RECOMMENDED),
    }

    const flightFiltersModalLabelMap: Record<string, string> = {
      [FlightFilterType.STOPS]: getFlightFilterModalLabel(intl, FlightFilterType.STOPS),
      [FlightFilterType.AIRLINES]: getFlightFilterModalLabel(intl, FlightFilterType.AIRLINES),
      [FlightFilterType.STOP_DURATIONS]: getFlightFilterModalLabel(intl, FlightFilterType.STOP_DURATIONS),
      [FlightFilterType.CABIN_TYPES]: getFlightFilterModalLabel(intl, FlightFilterType.CABIN_TYPES),
      [FlightFilterType.DURATIONS]: getFlightFilterModalLabel(intl, FlightFilterType.DURATIONS),
      [FlightFilterType.PRICES]: getFlightFilterModalLabel(intl, FlightFilterType.PRICES),
      [FlightFilterType.TIMES]: getFlightFilterModalLabel(intl, FlightFilterType.TIMES),
      [FlightFilterType.RECOMMENDED]: getFlightFilterModalLabel(intl, FlightFilterType.RECOMMENDED),
      [FlightFilterType.BAGS]: getFlightFilterModalLabel(intl, FlightFilterType.BAGS),
      [FlightFilterType.MEMBER_PRICES]: getFlightFilterModalLabel(intl, FlightFilterType.MEMBER_PRICES),
      [FlightFilterType.STOP_OVER_CITIES]: getFlightFilterModalLabel(intl, FlightFilterType.STOP_OVER_CITIES),
      [FlightFilterType.ALLIANCES]: getFlightFilterModalLabel(intl, FlightFilterType.ALLIANCES),
      [FlightFilterType.APPLIED_FILTERS]: getFlightFilterModalLabel(intl, FlightFilterType.APPLIED_FILTERS),
    }

    const flightFilters: Array<{ label: string; value: FlightFilterType }> = [
      { label: flightFiltersLabelMap[FlightFilterType.STOPS], value: FlightFilterType.STOPS },
      { label: flightFiltersLabelMap[FlightFilterType.AIRLINES], value: FlightFilterType.AIRLINES },
      { label: flightFiltersLabelMap[FlightFilterType.STOP_DURATIONS], value: FlightFilterType.STOP_DURATIONS },
      { label: flightFiltersLabelMap[FlightFilterType.CABIN_TYPES], value: FlightFilterType.CABIN_TYPES },
      { label: flightFiltersLabelMap[FlightFilterType.DURATIONS], value: FlightFilterType.DURATIONS },
      { label: flightFiltersLabelMap[FlightFilterType.BAGS], value: FlightFilterType.BAGS },
      { label: flightFiltersLabelMap[FlightFilterType.MEMBER_PRICES], value: FlightFilterType.MEMBER_PRICES },
      { label: flightFiltersLabelMap[FlightFilterType.STOP_OVER_CITIES], value: FlightFilterType.STOP_OVER_CITIES },
      { label: flightFiltersLabelMap[FlightFilterType.ALLIANCES], value: FlightFilterType.ALLIANCES },
    ]

    return {
      flightFiltersLabelMap,
      flightFiltersModalLabelMap,
      flightFilters,
    }
  }, [intl])
}

export default useFlightFilterLabels
